import React, {
    forwardRef,
    useCallback,
    useImperativeHandle,
    useState,
} from "react";
import type { FC, Ref, ChangeEvent } from "react";

import cn from "classnames";

import { useInputContainerStore } from "../../../../helpers/common/CoreInputText/store/inputContainerStore";
import { useStore } from "../../store/store";
import { createDisplayNameForInputTextAreaElement } from "../../utils/getDisplayName";
import { createTestIdForInputTextAreaElement } from "../../utils/getTestId";

import type { ITextAreaProps } from "../../types";

import classes from "./InputTextAreaTextArea.styl";

export const TEXTAREA_TEST_ID = createTestIdForInputTextAreaElement("textarea");

export const TextArea: FC = forwardRef<HTMLTextAreaElement, ITextAreaProps>(
    ({ className, ...inputProps }, ref: Ref<HTMLTextAreaElement | null>) => {
        const { textAreaRef } = useStore();
        const { inputProps: inputContainerStore } = useInputContainerStore();
        const { value, defaultValue } = inputProps;
        const [innerValue, setInnerValue] = useState(defaultValue);

        useImperativeHandle(ref, () => textAreaRef.current);

        const isUncontrolledInput = value === undefined;

        const handleOnChange = useCallback(
            (e: ChangeEvent<HTMLTextAreaElement>) => {
                if (isUncontrolledInput) {
                    setInnerValue(e?.target?.value);
                }

                inputProps?.onChange?.(e);
            },
            [inputProps, isUncontrolledInput],
        );

        return (
            /**
             * data-value будет использоваться в псевдо-элементе, что позволит увеличивать textarea постепенно и
             * с правильным позиционированием при alignment=end
             */
            <div
                className={classes["wrapper"]}
                style={inputContainerStore?.style}
                data-value={isUncontrolledInput ? innerValue : value}
            >
                <textarea
                    {...inputProps}
                    onChange={handleOnChange}
                    className={cn(classes.textarea, className)}
                    data-tsid={TEXTAREA_TEST_ID}
                    ref={textAreaRef}
                />
            </div>
        );
    },
);

// В данном случае, Input - тот компонент, который ожидает CoreInputText
TextArea.displayName = createDisplayNameForInputTextAreaElement("Input");
